import { supabase } from '../supabase-client';
import { withProfiles } from './client_profiles';
import type { Database } from '../types/database';
import { getClientDocumentCount } from './client_documents';

type LawyerPlanInfo = { priority: number; planType: string | null };

/** Get priority_in_search and plan_type for lawyers (Pro badge, directory ordering) */
async function getLawyerPlanInfo(
  lawyerIds: string[]
): Promise<Map<string, LawyerPlanInfo>> {
  if (lawyerIds.length === 0) return new Map();

  const { data: subs } = await supabase
    .from('subscriptions')
    .select('user_id, plan_type')
    .in('user_id', lawyerIds)
    .in('status', ['active', 'trialing']);

  if (!subs?.length) return new Map();

  const planTypes = [...new Set(subs.map((s) => s.plan_type))];
  const { data: limits } = await supabase
    .from('plan_limits')
    .select('plan_type')
    .in('plan_type', planTypes);

  // priority_in_search: lawyer_free=0, lawyer_professional=50, lawyer_professional_pro=100 (from plans.ts / migration 026)
  const priorityByPlanType: Record<string, number> = {
    lawyer_free: 0,
    lawyer_professional: 50,
    lawyer_professional_pro: 100,
  };

  const priorityByPlan = new Map(
    (limits || []).map((l) => [
      l.plan_type,
      priorityByPlanType[l.plan_type] ?? 0,
    ])
  );

  const map = new Map<string, LawyerPlanInfo>();
  for (const s of subs) {
    const p = priorityByPlan.get(s.plan_type) ?? 0;
    const current = map.get(s.user_id);
    if (!current || p > current.priority) {
      map.set(s.user_id, { priority: p, planType: s.plan_type });
    }
  }
  return map;
}

export type LawyerProfile = Database['public']['Tables']['lawyer_profiles']['Row'];
export type LawyerProfileInsert = Database['public']['Tables']['lawyer_profiles']['Insert'];
export type LawyerProfileUpdate = Database['public']['Tables']['lawyer_profiles']['Update'];

/**
 * Get lawyer profile by user ID
 */
export async function getLawyerProfile(userId: string) {
  const { data, error } = await supabase
    .from('lawyer_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get complete lawyer profile with user data and plan_type (for Pro badge)
 */
export async function getCompleteLawyerProfile(userId: string) {
  const { data, error } = await supabase
    .from('lawyer_profiles')
    .select(`
      *,
      profiles!inner(
        id,
        email,
        full_name,
        country,
        profile_image_url,
        created_at
      )
    `)
    .eq('id', userId)
    .single();

  if (error) throw error;

  const planInfo = await getLawyerPlanInfo([userId]);
  const planType = planInfo.get(userId)?.planType ?? null;

  return { ...data, plan_type: planType };
}

/**
 * Create or update lawyer profile
 */
export async function upsertLawyerProfile(
  userId: string,
  profile: Omit<LawyerProfileInsert, 'id'>
) {
  const { data, error } = await supabase
    .from('lawyer_profiles')
    .upsert({
      id: userId,
      ...profile,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Update lawyer profile
 */
export async function updateLawyerProfile(
  userId: string,
  updates: LawyerProfileUpdate
) {
  const { data, error } = await supabase
    .from('lawyer_profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Get verified lawyers (public directory)
 * Pro plan lawyers appear first (priority_in_search), then by created_at
 * Filters out lawyers with allow_search = false
 */
export async function getVerifiedLawyers(country?: string) {
  let query = supabase
    .from('lawyer_profiles')
    .select(`
      *,
      profiles!inner(
        id,
        email,
        full_name,
        country,
        profile_image_url
      )
    `)
    .eq('is_verified', true)
    .eq('accept_cases', true)
    .or('allow_search.eq.true,allow_search.is.null');

  if (country) {
    query = query.eq('profiles.country', country);
  }

  const { data, error } = await query;

  if (error) throw error;
  if (!data?.length) return data;

  const planInfo = await getLawyerPlanInfo(data.map((l) => l.id));
  return data
    .sort((a, b) => {
      const pa = planInfo.get(a.id)?.priority ?? 0;
      const pb = planInfo.get(b.id)?.priority ?? 0;
      if (pb !== pa) return pb - pa;
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    })
    .map((l) => ({
      ...l,
      plan_type: planInfo.get(l.id)?.planType ?? null,
    }));
}

/**
 * Search lawyers by practice area
 * Pro plan lawyers appear first (priority_in_search), then by years_of_experience
 */
export async function searchLawyersByArea(practiceArea: string, country?: string) {
  let query = supabase
    .from('lawyer_profiles')
    .select(`
      *,
      profiles!inner(
        id,
        email,
        full_name,
        country,
        profile_image_url
      )
    `)
    .contains('practice_areas', [practiceArea])
    .eq('is_verified', true)
    .eq('accept_cases', true);

  if (country) {
    query = query.eq('profiles.country', country);
  }

  const { data, error } = await query;

  if (error) throw error;
  if (!data?.length) return data;

  const planInfo = await getLawyerPlanInfo(data.map((l) => l.id));
  return data
    .sort((a, b) => {
      const pa = planInfo.get(a.id)?.priority ?? 0;
      const pb = planInfo.get(b.id)?.priority ?? 0;
      if (pb !== pa) return pb - pa;
      return (b.years_of_experience ?? 0) - (a.years_of_experience ?? 0);
    })
    .map((l) => ({
      ...l,
      plan_type: planInfo.get(l.id)?.planType ?? null,
    }));
}

/**
 * Check if bar number is unique
 */
export async function isBarNumberUnique(barNumber: string, excludeUserId?: string) {
  let query = supabase
    .from('lawyer_profiles')
    .select('id')
    .eq('bar_number', barNumber);

  if (excludeUserId) {
    query = query.neq('id', excludeUserId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data.length === 0;
}

/**
 * Get lawyers accepting clients
 */
export async function getLawyersAcceptingClients(
  country?: string,
  practiceAreas?: string[]
) {
  let query = supabase
    .from('lawyer_profiles')
    .select(`
      *,
      profiles!inner(
        id,
        email,
        full_name,
        country,
        profile_image_url
      )
    `)
    .eq('is_verified', true)
    .eq('accept_clients', true);

  if (country) {
    query = query.eq('profiles.country', country);
  }

  if (practiceAreas && practiceAreas.length > 0) {
    query = query.overlaps('practice_areas', practiceAreas);
  }

  const { data, error } = await query.limit(50);

  if (error) throw error;
  if (!data?.length) return data;

  const planInfo = await getLawyerPlanInfo(data.map((l) => l.id));
  return data
    .sort((a, b) => {
      const pa = planInfo.get(a.id)?.priority ?? 0;
      const pb = planInfo.get(b.id)?.priority ?? 0;
      if (pb !== pa) return pb - pa;
      return (b.years_of_experience ?? 0) - (a.years_of_experience ?? 0);
    })
    .slice(0, 20)
    .map((l) => ({
      ...l,
      plan_type: planInfo.get(l.id)?.planType ?? null,
    }));
}

/**
 * Get clients associated with a lawyer (from cases)
 */
export async function getClientsByLawyer(lawyerId: string) {
  // Get unique clients from cases
  const { data, error } = await supabase
    .from('cases')
    .select(`
      client_id,
      client_profiles!inner(
        id,
        client_type,
        phone,
        company_name,
        full_name,
        email,
        country
      )
    `)
    .eq('lawyer_id', lawyerId);

  if (error) throw error;

  // Remove duplicates based on client_id and normalize with profiles shape
  const seenClients = new Set<string>();
  const uniqueClients = (data || []).reduce<ReturnType<typeof withProfiles>[]>((acc, current) => {
    const clientId = current.client_profiles.id;
    if (!seenClients.has(clientId)) {
      seenClients.add(clientId);
      acc.push(withProfiles(current.client_profiles));
    }
    return acc;
  }, []);

  // Get counts for all clients in parallel
  const clientsWithCounts = await Promise.all(
    uniqueClients.map(async (client) => {
      const clientId = (client as unknown as { id: string }).id;
      // Get cases count
      const { count: casesCount } = await supabase
        .from('cases')
        .select('*', { count: 'exact', head: true })
        .eq('client_id', clientId);

      // Get documents count using the helper function
      let documentsCount = 0;
      try {
        documentsCount = await getClientDocumentCount(clientId);
      } catch (error) {
        console.error(`Error getting document count for client ${clientId}:`, error);
      }

      return {
        ...client,
        cases_count: casesCount || 0,
        documents_count: documentsCount || 0,
      };
    })
  );

  return clientsWithCounts;
}

